/**
 * Price-tier BATCH entry + EQP cost copy — the pure decisions behind the two
 * throughput asks the GM raised on 2026-08-11:
 *
 *   "adding multiple price tiers … should be able to add as many as we want at
 *    once — costing a lot of time"
 *   "copy the net price to the other tiers if we have EQP"
 *
 * DB-free on purpose, like order-money and duplicate-fee: the offline gate
 * EXECUTES these functions against fixtures rather than regex-matching the
 * component that calls them. A grep-only gate on a form is exactly how the
 * tier row's cost regex drifted from the API's and made every auto-populated
 * tier unsaveable (§L5c).
 *
 * ─── The money rule this file exists to hold ──────────────────────────────
 *
 * `copyCostWrites` writes COST AND NOTHING ELSE. `presentation_item_tiers`
 * .margin_pct is a GENERATED column (sell − cost, re-derived by Postgres on
 * every UPDATE), so moving cost alone silently re-prices the MARGIN and leaves
 * the client-facing SELL exactly where the rep put it. That is the honest
 * automation of what she does by hand today — type the same net cost into five
 * boxes — and it is deliberately NOT the existing "Override cost" button, which
 * DELETES every tier and regenerates a 40%-margin curve, discarding her
 * quantities and her sell prices.
 *
 * A sell price may already have been sent to a client. Never move it to keep a
 * margin looking tidy.
 */

import {
  COST_INPUT_RE,
  isValidCostInput,
  normalizeMoneyInput,
  toMills,
} from "./order-money.js";

/** SELL stays two decimals — client-facing prices, CEO 2026-08-10. Mirrors
 *  `numericString` in routes/presentations.ts, minus the leading `-` that
 *  schema tolerates: a negative sell on a quote is never a rep's intent. */
const SELL_INPUT_RE = /^\d+(\.\d{1,2})?$/;

export interface TierDraft {
  quantity: string;
  wholesale: string;
  sell: string;
  hidden?: boolean;
}

/** Server-shaped: the exact body POST /presentation_items/:id/tiers accepts. */
export interface TierWrite {
  quantity: number;
  wholesale_cost: string;
  sell_price: string;
  is_hidden_from_client: boolean;
}

export interface TierBatchError {
  /** Index into the ORIGINAL drafts array, so the form can mark the real row.
   *  −1 means the batch as a whole rather than any one row. */
  row: number;
  field: "quantity" | "wholesale" | "sell" | "batch";
  message: string;
}

export type TierBatchPlan =
  | { ok: true; writes: TierWrite[] }
  | { ok: false; errors: TierBatchError[] };

/** All three money/qty boxes empty — the rep clicked "Add row" once too often.
 *  Dropped silently. A row with ANY field filled is validated in full, so a
 *  quantity typed without a price errors instead of vanishing. */
function isBlankRow(d: TierDraft): boolean {
  return (
    d.quantity.trim() === "" &&
    d.wholesale.trim() === "" &&
    d.sell.trim() === ""
  );
}

/**
 * Validate every draft row and return the POST bodies, or EVERY error at once.
 *
 * ⚠️ All-or-nothing by design. The API has no bulk tier endpoint, so the client
 * posts N times; a row that fails server-side mid-flight leaves a PARTIAL price
 * grid on a live quote. Validating the whole batch here — including duplicate
 * quantities, which the server answers with a 409 — is what keeps the common
 * failures out of the network entirely.
 *
 * Errors are returned as a LIST, not first-failure: fixing five typos one
 * round-trip at a time is the friction she reported, restated.
 */
export function planTierBatch(args: {
  drafts: readonly TierDraft[];
  /** Quantities already on the item. A tier qty is unique per item at the DB
   *  level, so these collide the same way in-draft duplicates do. */
  existingQuantities: readonly number[];
}): TierBatchPlan {
  const errors: TierBatchError[] = [];
  const writes: Array<TierWrite & { row: number }> = [];

  const existing = new Set(args.existingQuantities);
  // qty → the FIRST draft row that claimed it, so the duplicate is reported on
  // the later row (the one the rep just typed) rather than the earlier one.
  const claimed = new Map<number, number>();

  let considered = 0;

  args.drafts.forEach((draft, row) => {
    if (isBlankRow(draft)) return;
    considered += 1;

    const qRaw = draft.quantity.trim();
    const q = Number.parseInt(qRaw, 10);
    // Number.parseInt("50abc") is 50 — compare the round-trip so junk is caught.
    const qValid =
      qRaw !== "" && Number.isInteger(q) && q > 0 && String(q) === qRaw;
    if (!qValid) {
      errors.push({
        row,
        field: "quantity",
        message: "Quantity must be a positive whole number.",
      });
    } else if (q > 10_000_000) {
      // Mirrors addTierBody's .max(10_000_000). Without this the batch posts
      // and the server answers an opaque 400 on one row of five.
      errors.push({
        row,
        field: "quantity",
        message: "Quantity is too large.",
      });
    } else if (existing.has(q)) {
      errors.push({
        row,
        field: "quantity",
        message: `This item already has a ${q.toLocaleString()} tier.`,
      });
    } else if (claimed.has(q)) {
      errors.push({
        row,
        field: "quantity",
        message: `Quantity ${q.toLocaleString()} is on more than one row.`,
      });
    } else {
      claimed.set(q, row);
    }

    // Normalise BEFORE sending, not just before validating. isValidCostInput
    // strips thousands separators internally, so "1,250.00" passed the old
    // single-row form's check and was then posted verbatim — straight into a
    // 400 from costString, which has no such tolerance. The boxes are
    // type="text" inputMode="decimal", so a comma is genuinely typeable.
    const w = normalizeMoneyInput(draft.wholesale);
    if (!isValidCostInput(draft.wholesale)) {
      errors.push({
        row,
        field: "wholesale",
        message: "Wholesale cost must be a number (e.g. 4.25).",
      });
    } else if (toMills(w) <= 0) {
      // ABSENT IS NOT ZERO (§L3) — and neither is negative. A 0 cost reads as
      // free goods to the PO zero-cost warning and to margin; the old
      // single-row form accepted both, because COST_INPUT_RE tolerates a
      // leading "-" for the server's own stored-value round-trips.
      errors.push({
        row,
        field: "wholesale",
        message: "Wholesale cost must be greater than zero.",
      });
    }

    const s = normalizeMoneyInput(draft.sell);
    if (!SELL_INPUT_RE.test(s)) {
      errors.push({
        row,
        field: "sell",
        message: "Sell price must be a number (e.g. 9.95).",
      });
    }

    if (qValid && isValidCostInput(draft.wholesale) && SELL_INPUT_RE.test(s)) {
      writes.push({
        row,
        quantity: q,
        wholesale_cost: w,
        sell_price: s,
        is_hidden_from_client: draft.hidden ?? false,
      });
    }
  });

  if (considered === 0) {
    return {
      ok: false,
      errors: [
        { row: -1, field: "batch", message: "Enter at least one tier." },
      ],
    };
  }
  if (errors.length > 0) return { ok: false, errors };

  // Ascending quantity, not draft order. addPresentationTier assigns
  // position = max(position) + 1 per insert, so posting in grid order is what
  // makes the stored positions read the way a price grid reads.
  return {
    ok: true,
    writes: writes
      .sort((a, b) => a.quantity - b.quantity)
      .map(({ row: _row, ...w }) => w),
  };
}

export interface TierCostRow {
  id: string;
  wholesaleCost: string;
}

/** PATCH body for one tier. ONE field. See the money rule at the top of the
 *  file — the absence of `sell_price` here is the guarantee, and the offline
 *  gate asserts the key set, not just the value. */
export interface TierCostWrite {
  tierId: string;
  wholesale_cost: string;
}

/**
 * "Copy the net price to the other tiers if we have EQP" — the writes needed to
 * put ONE tier's wholesale cost onto every other tier of the same item.
 *
 * Excludes the source row and any tier already at that cost NUMERICALLY —
 * "1.170" and "1.17" are the same money, and re-writing one as the other would
 * spend an audit event to change nothing.
 */
export function copyCostWrites(args: {
  tiers: readonly TierCostRow[];
  sourceTierId: string;
}): TierCostWrite[] {
  const source = args.tiers.find((t) => t.id === args.sourceTierId);
  if (!source) return [];

  const cost = normalizeMoneyInput(source.wholesaleCost);
  // A stored cost that cannot survive the wire is not copyable. Bail rather
  // than posting N bodies the server will reject one at a time.
  if (!COST_INPUT_RE.test(cost) || toMills(cost) <= 0) return [];
  const costMills = toMills(cost);

  return args.tiers
    .filter((t) => t.id !== args.sourceTierId)
    .filter((t) => toMills(t.wholesaleCost) !== costMills)
    .map((t) => ({ tierId: t.id, wholesale_cost: cost }));
}
